import { GlobalConfigType, setGlobalConfig } from 'actions'
import cloneDeep from 'clone-deep'
import { OptionModal } from 'components/Modals/OptionModal'
import { type InputType, SettingKey } from 'config'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { getOptionValues, IOptionType } from 'utils/general'
import { RenderInput } from 'utils/RenderInput'

import {
  AccountingNormalBalanceOptions,
  AccountingStatementOptions,
  getAccountTypeLabel,
  IAccountingAccount,
} from './types'

export const defaultInputs = (): Record<string, InputType> => {
  return {
    no: {
      inputType: 'text',
      type: 'number',
      title: 'No',
      required: true,
    },
    type: {
      inputType: 'select',
      title: 'Account Type',
      hasDefaultOption: true,
      defaultOptionText: '- Select -',
      options: [],
      required: true,
    },
    name: {
      inputType: 'text',
      title: 'Account Name',
      required: true,
    },
    number: {
      inputType: 'text',
      type: 'number',
      title: 'Account Number',
      required: true,
    },
    section: {
      inputType: 'select',
      title: 'Section',
      required: false,
      hasDefaultOption: true,
      defaultOptionText: '- Select -',
      options: [],
    },
    parentId: {
      inputType: 'select',
      title: 'Parent Account',
      hasDefaultOption: true,
      defaultOptionText: 'None',
      options: {},
      required: false,
    },
    normalBalance: {
      inputType: 'select',
      title: 'Normal Balance',
      hasDefaultOption: true,
      defaultOptionText: '- Select -',
      options: AccountingNormalBalanceOptions,
      required: false,
    },
    statement: {
      inputType: 'select',
      title: 'Statement',
      hasDefaultOption: true,
      defaultOptionText: '- Select -',
      options: AccountingStatementOptions,
      required: false,
    },
    notes: {
      inputType: 'textarea',
      title: 'Notes',
      required: false,
      span: 'full',
    },
    description: {
      inputType: 'textarea',
      title: 'Description',
      required: false,
      span: 'full',
    },
  }
}

export const EditAccountDialog = ({
  item,
  onClose,
  allAccounts,
}: {
  item: IAccountingAccount | null
  onClose: Function
  allAccounts: IAccountingAccount[]
}) => {
  const dispatch = useDispatch()
  const sectionOptions = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.ACCOUNT_SECTIONS] || [])
  const accountTypeOptions = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.ACCOUNTING_ACCOUNT_TYPES] || [],
  )

  const [isLoading, setLoading] = useState(false)
  const [optionsModal, setOptionsModal] = useState<SettingKey | null>(null)
  const [inputs, setInputs] = useState(defaultInputs())

  useEffect(() => {
    const newInputs = cloneDeep(defaultInputs())

    if (item) {
      Object.keys(newInputs).forEach((key) => {
        if (key === 'type') {
          newInputs[key].value = getAccountTypeLabel(item.type)
        } else {
          newInputs[key].value = (item as any)[key]
        }
      })
    }

    updateParentOptions(newInputs, item?.type, item?.id)
    setInputs(newInputs)
  }, [item, allAccounts])

  const updateParentOptions = (
    currentInputs: Record<string, InputType>,
    selectedType?: string,
    currentItemId?: number,
  ) => {
    if (!selectedType) {
      currentInputs.parentId.options = {}
      return
    }

    const filteredAccounts = allAccounts.filter(
      (acc) =>
        (acc.type === selectedType || getAccountTypeLabel(acc.type) === selectedType) &&
        (!currentItemId || acc.id !== currentItemId),
    )

    const options: Record<string, string> = {}
    filteredAccounts.forEach((acc) => {
      options[acc.id.toString()] = `${acc.name} (${acc.number})`
    })

    currentInputs.parentId.options = options
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value

    if (key === 'type') {
      updateParentOptions(newInputs, value, item?.id)
      newInputs.parentId.value = ''
    }

    setInputs(newInputs)
  }

  const onSubmit = async () => {
    let hasError = false

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      if (newInputs[key].value?.trim) newInputs[key].value = newInputs[key].value.trim()
      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    if (hasError) {
      setInputs(newInputs)
      return
    }

    setLoading(true)

    const submitData = {
      id: item ? item.id : -Date.now(),
      ...data,
    }

    await onClose(submitData)
    setLoading(false)
  }

  const optionsModalTitle =
    optionsModal === SettingKey.ACCOUNTING_ACCOUNT_TYPES ? 'Update Account Type options' : 'Update Section options'

  return (
    <>
      <Modal
        isOpen
        title={item ? 'Edit Account' : 'Create Account'}
        loading={isLoading}
        onOk={onSubmit}
        onClose={() => onClose(null)}
        titleOkay="Submit"
        titleCancel=""
      >
        <div className="w-196 relative grid grid-cols-2 gap-4">
          {Object.keys(inputs).map((key, index) => {
            const input = inputs[key]
            if (key === 'type') {
              input.additionalElements = (
                <span className="btn-link" onClick={() => setOptionsModal(SettingKey.ACCOUNTING_ACCOUNT_TYPES)}>
                  Add Account Type
                </span>
              )
              ;(input as any).options = getOptionValues(accountTypeOptions)
            }
            if (key === 'section') {
              input.additionalElements = (
                <span className="btn-link" onClick={() => setOptionsModal(SettingKey.ACCOUNT_SECTIONS)}>
                  Add Section
                </span>
              )
              ;(input as any).options = getOptionValues(sectionOptions)
            }
            return (
              <div className={`input mb-4 col-span-${input.span || 1}`} key={index}>
                <RenderInput input={input} Key={key} onChange={onChange} />
              </div>
            )
          })}
        </div>
      </Modal>
      {optionsModal && (
        <OptionModal
          title={optionsModalTitle}
          settingKey={optionsModal}
          onClose={(values: IOptionType[]) => {
            if (values) {
              const configType =
                optionsModal === SettingKey.ACCOUNTING_ACCOUNT_TYPES
                  ? GlobalConfigType.ACCOUNTING_ACCOUNT_TYPES
                  : GlobalConfigType.ACCOUNT_SECTIONS
              dispatch(setGlobalConfig(configType, values))
            }
            setOptionsModal(null)
          }}
          childLevel={1}
        />
      )}
    </>
  )
}
